(function () {
  'use strict';

  if (window.VaillantSwymWishlist) {
    window.VaillantSwymWishlist.syncButtons();
    return;
  }

  const state = {
    swat: null,
    mode: null,
    selectedList: null,
    wishlisted: new Set(),
    pending: new Set(),
    errors: new Set(),
    queuedActions: [],
    loading: true,
    initialized: false,
    ready: false,
  };
  let toastTimer;

  const itemKey = (product) => `${String(product.empi)}:${String(product.epi)}`;

  function productFromButton(button) {
    return {
      epi: Number(button.dataset.variantId),
      empi: Number(button.dataset.productId),
      du: button.dataset.productUrl,
      qty: 1,
      source: button.dataset.swymSource || 'collections-grid',
    };
  }

  function getButtons(root) {
    const buttons = [];

    if (root instanceof Element && root.matches('[data-vaillant-wishlist-button]')) {
      buttons.push(root);
    }

    if (root.querySelectorAll) {
      buttons.push(...root.querySelectorAll('[data-vaillant-wishlist-button]'));
    }

    return buttons;
  }

  function updateButton(button) {
    const product = productFromButton(button);
    const key = itemKey(product);
    const isWishlisted = state.wishlisted.has(key);
    const isPending = state.pending.has(key);
    const hasError = state.errors.has(key);
    const title = button.dataset.productTitle || 'Product';
    const isAddOnly = button.hasAttribute('data-vaillant-wishlist-add-only');
    const action = isWishlisted ? 'Remove' : 'Add';
    const label = button.querySelector('.vaillant-cart-drawer__text-button-label');

    if (label && button.classList.contains('vaillant-cart-drawer__text-button--save')) {
      label.textContent = isWishlisted ? 'Saved' : 'Save';
    }

    button.classList.toggle('is-wishlisted', isWishlisted);
    button.classList.toggle('is-loading', state.loading || isPending);
    button.classList.toggle('is-error', hasError);
    button.setAttribute('aria-pressed', String(isWishlisted));
    button.setAttribute('aria-busy', String(state.loading || isPending));

    if (isAddOnly) {
      button.setAttribute(
        'aria-label',
        isWishlisted ? `${title} saved to wishlist` : `Save ${title} to wishlist`
      );
      button.disabled = isPending || isWishlisted;
    } else {
      button.setAttribute('aria-label', `${action} ${title} ${isWishlisted ? 'from' : 'to'} wishlist`);
      button.disabled = isPending;
    }
  }

  function syncButtons(root = document) {
    getButtons(root).forEach(updateButton);
  }

  function announce(message, tone = 'success') {
    document.querySelectorAll('[data-vaillant-wishlist-status]').forEach((status) => {
      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = message;
      });
    });

    let toast = document.querySelector('[data-vaillant-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'vaillant-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('data-vaillant-wishlist-toast', '');
      document.body.append(toast);
    }

    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.dataset.tone = tone;
    toast.classList.remove('is-visible');
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  function selectList(lists) {
    return [...lists].reverse().find((list) => !list.lty || list.lty === 'wl') || null;
  }

  function updateWishlistState(contents = state.selectedList?.listcontents || []) {
    state.wishlisted = new Set(contents.map(itemKey));
  }

  function fetchWishlist() {
    return new Promise((resolve, reject) => {
      state.swat.fetch(resolve, reject);
    });
  }

  function fetchLists() {
    return new Promise((resolve, reject) => {
      state.swat.fetchLists({
        callbackFn: resolve,
        errorFn: reject,
      });
    });
  }

  function createDefaultList() {
    return new Promise((resolve, reject) => {
      state.swat.createList({ lname: 'Wishlist', lty: 'wl' }, resolve, reject);
    });
  }

  function addToList(listId, product) {
    return new Promise((resolve, reject) => {
      state.swat.addToList(listId, product, resolve, reject);
    });
  }

  function deleteFromList(listId, product) {
    return new Promise((resolve, reject) => {
      state.swat.deleteFromList(listId, product, resolve, reject);
    });
  }

  function addToWishlist(product) {
    return new Promise((resolve, reject) => {
      state.swat.addToWishList(product, resolve, reject);
    });
  }

  function removeFromWishlist(product) {
    return new Promise((resolve, reject) => {
      state.swat.removeFromWishList(product, resolve, reject);
    });
  }

  async function refreshState() {
    if (!state.swat) return;

    state.loading = true;
    syncButtons();

    try {
      if (state.mode === 'legacy') {
        const items = await fetchWishlist();
        updateWishlistState(Array.isArray(items) ? items : []);
      } else {
        const lists = await fetchLists();
        state.selectedList = selectList(Array.isArray(lists) ? lists : []);
        updateWishlistState();
      }
    } catch (error) {
      console.error('Unable to load the Swym wishlist.', error);
      announce('Wishlist could not be loaded. Please try again.', 'error');
    } finally {
      state.loading = false;
      syncButtons();
      flushQueuedActions();
    }
  }

  async function ensureList() {
    if (state.selectedList?.lid) return state.selectedList;

    state.selectedList = await createDefaultList();
    updateWishlistState();
    return state.selectedList;
  }

  async function addProductToWishlist(button, product) {
    const key = itemKey(product);

    if (state.mode === 'legacy') {
      await addToWishlist(product);
    } else {
      const list = await ensureList();
      await addToList(list.lid, product);
    }

    state.wishlisted.add(key);
    announce(`${button.dataset.productTitle || 'Product'} added to wishlist.`);
  }

  async function removeProductFromWishlist(button, product) {
    const key = itemKey(product);

    if (state.mode === 'legacy') {
      await removeFromWishlist(product);
    } else {
      const list = await ensureList();
      await deleteFromList(list.lid, product);
    }

    state.wishlisted.delete(key);
    announce(`${button.dataset.productTitle || 'Product'} removed from wishlist.`);
  }

  async function addToWishlistOnly(button, product) {
    const key = itemKey(product);
    if (state.wishlisted.has(key)) return;

    state.pending.add(key);
    state.errors.delete(key);
    syncButtons();

    try {
      await addProductToWishlist(button, product);
    } catch (error) {
      console.error('Unable to update the Swym wishlist.', error);
      state.errors.add(key);
      announce('Wishlist could not be updated. Please try again.', 'error');
      window.setTimeout(() => {
        state.errors.delete(key);
        syncButtons();
      }, 3000);
    } finally {
      state.pending.delete(key);
      syncButtons();
    }
  }

  async function toggleWishlist(button, product) {
    const key = itemKey(product);
    const wasWishlisted = state.wishlisted.has(key);

    state.pending.add(key);
    state.errors.delete(key);
    syncButtons();

    try {
      if (wasWishlisted) {
        await removeProductFromWishlist(button, product);
      } else {
        await addProductToWishlist(button, product);
      }
    } catch (error) {
      console.error('Unable to update the Swym wishlist.', error);
      state.errors.add(key);
      announce('Wishlist could not be updated. Please try again.', 'error');
      window.setTimeout(() => {
        state.errors.delete(key);
        syncButtons();
      }, 3000);
    } finally {
      state.pending.delete(key);
      syncButtons();
    }
  }

  function queueAction(button, product) {
    const key = itemKey(product);
    if (state.pending.has(key)) return;

    state.pending.add(key);
    state.queuedActions.push({ button, product });
    syncButtons();
  }

  function flushQueuedActions() {
    if (!state.swat || state.loading || !state.queuedActions.length) return;

    const actions = state.queuedActions.splice(0);
    actions.forEach(({ button, product }) => {
      state.pending.delete(itemKey(product));
      if (button.hasAttribute('data-vaillant-wishlist-add-only')) {
        addToWishlistOnly(button, product);
      } else {
        toggleWishlist(button, product);
      }
    });
  }

  function bindSwymEvents() {
    if (!state.swat.evtLayer?.addEventListener) return;

    ['sw:addedtowishlist', 'sw:removedfromwishlist'].forEach((eventName) => {
      state.swat.evtLayer.addEventListener(eventName, () => {
        window.setTimeout(refreshState, 150);
      });
    });

    const customerEvent = state.swat.JSEvents?.customerInfoRefreshed;
    if (customerEvent) {
      state.swat.evtLayer.addEventListener(customerEvent, refreshState);
    }

    const regidRefreshedEvent = state.swat.JSEvents?.regidRefreshed;
    if (regidRefreshedEvent) {
      state.swat.evtLayer.addEventListener(regidRefreshedEvent, () => {
        if (state.ready) refreshState();
      });
    }
  }

  function initialize(swat) {
    if (!swat || state.initialized) return;

    state.swat = swat;
    state.initialized = true;
    bindSwymEvents();

    const activateWishlist = () => {
      if (state.ready) return;

      state.ready = true;
      state.mode =
        typeof swat.fetch === 'function' &&
        typeof swat.addToWishList === 'function' &&
        typeof swat.removeFromWishList === 'function'
          ? 'legacy'
          : 'lists';

      if (swat.isRegistering && swat.JSEvents?.regidRefreshed) {
        state.loading = true;
        syncButtons();
        return;
      }

      refreshState();
    };

    const configLoadedEvent = swat.JSEvents?.configLoaded;
    if (configLoadedEvent && swat.evtLayer?.addEventListener) {
      swat.evtLayer.addEventListener(configLoadedEvent, activateWishlist);
    } else {
      activateWishlist();
    }

    if (document.querySelector('#swym-plugin.swym-ready, #swym-hosted-plugin.swym-ready')) {
      activateWishlist();
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-vaillant-wishlist-button]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const product = productFromButton(button);
    if (!state.swat || state.loading) {
      queueAction(button, product);
      return;
    }

    if (button.hasAttribute('data-vaillant-wishlist-add-only')) {
      addToWishlistOnly(button, product);
      return;
    }

    toggleWishlist(button, product);
  });

  document.addEventListener('shopify:section:load', (event) => syncButtons(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) syncButtons(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(initialize);

  let pollCount = 0;
  const swymPoll = window.setInterval(() => {
    pollCount += 1;

    if (window._swat) {
      window.clearInterval(swymPoll);
      initialize(window._swat);
    } else if (pollCount >= 40) {
      window.clearInterval(swymPoll);
    }
  }, 250);

  window.VaillantSwymWishlist = { syncButtons, refreshState };
  syncButtons();
})();
